// Zod schemas for everything the UI reads. They are the contract between the
// UI and the future ORCA API: today they validate mock data, later the same
// schemas validate real responses. Types are inferred from them (see types/).
import { z } from 'zod';

export const SignalType = z.enum(['whale', 'x', 'price', 'news', 'yt', 'ai', 'macro']);

/* ---------- market ---------- */
export const Coin = z.object({
  symbol: z.string(),
  name: z.string(),
  price: z.number(),
  decimals: z.number().int(),
  change24h: z.number(),
  /** mock-only: seeds the decorative sparkline until real series exist */
  sparkSeed: z.number(),
});
export const Sentiment = z.object({ value: z.number(), prev: z.number(), label: z.string() });
/** Quotes for every supported coin; the user's coins are picked by the account. */
export const Market = z.object({
  universe: z.array(Coin),
  sentiment: z.object({ crypto: Sentiment, stocks: Sentiment }),
});

/* ---------- dashboard counters shown in heroes / tiles ---------- */
// Counts of signals, alerts, transfers and news are not here: they are derived
// from the same data the pages show (hooks/counts.ts), so numbers always match.
export const Dashboard = z.object({
  signalsLastHour: z.number(),
  /** `updatedAt` — ISO; next deliveries are computed from the user's intervals */
  feed: z.object({ total: z.number(), hidden: z.number(), updatedAt: z.string() }),
  xRadar: z.object({ total: z.number(), hidden: z.number(), accounts: z.number() }),
  youtube: z.object({ channels: z.number(), today: z.number() }),
  /** the last market overview that was sent (ISO) */
  marketOverview: z.object({ at: z.string(), bullets: z.array(z.tuple([z.string(), z.string()])) }),
});

/* ---------- portfolio ---------- */
/** `since` is a calendar date of the purchase, not a moment: 'YYYY-MM-DD', or 'YYYY-MM' when approximate */
/**
 * One purchase of a coin — the same facts the bot keeps per position:
 * quantity and price are always the user's (Orca never invents a price);
 * `date` is a calendar day ('YYYY-MM-DD') either named by the user
 * (`dateSource: 'user'`) or found by Orca from the price history for the
 * approximate `period` the user named (`dateSource: 'history'`, shown as «≈»);
 * `period` ('YYYY-MM') stays when no single day was confirmed; nothing — unknown.
 */
export const PurchaseLot = z.object({
  id: z.string(), qty: z.number(),
  /** absent — the user doesn't know it (the position is incomplete: no invested / P/L for this purchase) */
  price: z.number().optional(),
  /**
   * where the price comes from: the user typed it (default), or — a NEW web
   * feature, not in the bot yet — Orca estimated it from market history:
   * `history_close` — the day's close; `history_median` — the median daily
   * close of the named month. Estimated prices are always shown with «≈».
   */
  priceSource: z.enum(['user', 'history_close', 'history_median']).optional(),
  date: z.string().optional(), dateSource: z.enum(['user', 'history']).optional(), period: z.string().optional(),
  /** telegram_position: the purchase came from the position saved in Telegram */
  origin: z.enum(['web', 'telegram_position']).optional(),
});
/** A position = its purchases; qty, the weighted average price and the earliest
 *  known date are aggregated from the lots (by the API; the UI only displays them). */
export const Holding = z.object({
  symbol: z.string(), qty: z.number(), avgPrice: z.number(),
  /** the earliest known purchase: a day ('YYYY-MM-DD') or a month ('YYYY-MM'), and how it is known */
  since: z.string().optional(), sinceSource: z.enum(['user', 'history', 'period']).optional(),
  /** invested: Σ qty × price over purchases with a price; `pricedQty` — their quantity */
  cost: z.number(), pricedQty: z.number(),
  /** some purchases have an Orca-estimated price → average, invested and P/L are approximate */
  estimated: z.boolean().optional(),
  /** some purchases have no price → P/L is not calculated */
  incomplete: z.boolean().optional(),
  lots: z.array(PurchaseLot),
  /**
   * The ORCA API values the position itself (current price, value, P/L in
   * Decimal on the server); the UI shows these and never recomputes them.
   * Absent only in the DEV mock. `null` = not known (no quote / no price).
   */
  valuation: z.object({
    price: z.number().nullable(), change24h: z.number().nullable(), value: z.number().nullable(),
    pnl: z.number().nullable(), pnlPct: z.number().nullable(), day: z.number().nullable(), share: z.number().nullable(),
  }).optional(),
  /** telegram: shown from the position saved in Telegram (no Web purchases yet, or Telegram changed it later) */
  source: z.enum(['web', 'telegram']).optional(),
});
export const PortfolioTotals = z.object({
  value: z.number(), cost: z.number(), pnl: z.number().nullable(), pnlPct: z.number().nullable(),
  day: z.number(), dayPct: z.number().nullable(), estimated: z.boolean(),
  incomplete: z.array(z.string()), unpriced: z.array(z.string()),
});
export const Portfolio = z.object({ holdings: z.array(Holding), updatedAt: z.string(), totals: PortfolioTotals.optional() });

/* ---------- signals ---------- */
export const TimelineEvent = z.object({
  /** ISO moment */
  at: z.string(),
  lane: z.union([z.literal(0), z.literal(1)]),
  type: SignalType,
  title: z.string(),
  body: z.string(),
  note: z.string().optional(),
  metric: z.string().optional(),
  links: z.array(z.number()).optional(),
  /** Price change (%) within ~1 h after the event. Computed by the API from price history. */
  move: z.number().optional(),
  /** the event belongs to a feature the plan doesn't open: only its type and time are sent */
  locked: z.boolean().optional(),
  /** where the event comes from, when its feed has it: the article, the X post, the explorer */
  url: z.string().optional(),
  /** who / what published it: the news source, @handle, the network */
  source: z.string().optional(),
});
/** Scheduled event inside the next 7 days that may move the asset. */
export const Upcoming = z.object({
  /** ISO moment */
  at: z.string(),
  type: SignalType,
  title: z.string(),
  why: z.string(),
  imp: z.number().int().min(1).max(3),
  calendarId: z.string().optional(),
  /** the calendar is not in the plan: only the time and importance are sent */
  locked: z.boolean().optional(),
});
export const Range = z.tuple([z.number(), z.number()]);
export const AssetDay = z.object({
  /** the day's window on the timeline, ISO from–to */
  range: z.tuple([z.string(), z.string()]),
  /** demo only: a numeric corridor — ORCA's real forecast is a text and has none */
  forecast: z.object({ bear: Range, base: Range, bull: Range }).optional(),
  forecastText: z.string(),
  /** the stored forecast's facts (ORCA API): when it was generated, until when it holds */
  forecastMeta: z.object({
    generatedAt: z.string(), validUntil: z.string(), stale: z.boolean(), depth: z.string(),
    telegramSentAt: z.string().nullable(), positionNote: z.string().nullable(),
  }).optional(),
  /** no forecast stored for this coin yet (ORCA API) */
  forecastMissing: z.boolean().optional(),
  /** real 15-minute closes of the last 24 h, [ISO, price] (ORCA API) */
  series: z.array(z.tuple([z.string(), z.number()])).optional(),
  /** Orca's text forecast as the bot sends it (Plus 5–6, Premium 7–9 sentences) */
  forecastFull: z.string(),
  /** the 7-day forecast is not in the plan: the corridor and texts are not sent */
  forecastLocked: z.boolean().optional(),
  events: z.array(TimelineEvent),
  upcoming: z.array(Upcoming),
});
// Whale transfers for Signals come from the same feed as /whales (Alerts.whale.feed).
export const Signals = z.object({
  assets: z.record(AssetDay),
});

/* ---------- calendar ---------- */
export const CalEvent = z.object({
  id: z.string(), at: z.string(), title: z.string(), short: z.string(), region: z.string(),
  assets: z.array(z.string()), imp: z.number().int().min(1).max(3),
  prev: z.string().optional(), fc: z.string().optional(), fact: z.string().optional(),
  /** why it matters — demo text; the ORCA API has none (the block is hidden) */
  why: z.string().optional(),
});
/** One week; days and «today» are built in the user's time zone from `weekStart`. */
export const Calendar = z.object({
  weekStart: z.string(), // 'YYYY-MM-DD'
  events: z.array(CalEvent),
  /** the week's summary (demo HTML); the ORCA API has none */
  summary: z.string().optional(),
});

/* ---------- news ---------- */
export const NewsCategory = z.enum(['crypto', 'stocks', 'macro', 'world']);
export const NewsItem = z.object({
  id: z.number(),
  /** ISO moment of publication */
  at: z.string(),
  category: NewsCategory,
  tags: z.array(z.string()),
  /** importance, number of sources and merged duplicates: only when Orca knows them (never guessed) */
  imp: z.number().int().min(1).max(3).optional(),
  sources: z.number().optional(),
  dupes: z.number().optional(),
  title: z.string(),
  dek: z.string(),
  /** a longer brief, when there is more than the summary */
  body: z.string(),
  /** why it matters: Orca's explanation, absent until Orca has written one */
  why: z.string().optional(),
  image: z.string(),
  isNew: z.boolean(),
  /** First source + names of the others that reported it. */
  source: z.object({ name: z.string(), url: z.string() }),
  alsoIn: z.array(z.string()),
  /** the account's delivery section: coin_news / summary / global / macro */
  section: z.string().optional(),
  /** when a Telegram delivery of this account included it (ISO); reading on the Web never sets it */
  telegramIncludedAt: z.string().nullable().optional(),
});
/** «ORCA IDEAS · Идеи дня»: the bot's daily pick (Plus up to 7, Premium up to 10). */
export const Idea = z.object({ id: z.string(), at: z.string(), title: z.string(), dek: z.string(), tags: z.array(z.string()), source: z.object({ name: z.string(), url: z.string() }) });
export const News = z.object({ items: z.array(NewsItem), lastVisit: z.string(), ideas: z.array(Idea), /** when the API built this feed (ISO) */ updatedAt: z.string().optional() });
/** The News page: one server page of the account's feed and the page's counters (ORCA API; DEV mock emulates it). */
export const NewsFacets = z.object({
  /** by category, within the current period and coin */
  categories: z.record(z.string(), z.number()),
  /** by coin (+ «all»), within the current period and category */
  coins: z.record(z.string(), z.number()),
  /** the last 7 local days (0 = today), within the current coin and category */
  days: z.array(z.number()),
  unread: z.number(), telegram: z.number(), today: z.number(),
  /** every article of the 7 days, no filter */
  feed: z.number(),
});
export const NewsPage = z.object({
  items: z.array(NewsItem), page: z.number(), perPage: z.number(), pages: z.number(), total: z.number(),
  facets: NewsFacets, summaryTop: z.array(NewsItem), lastVisit: z.string(), updatedAt: z.string().optional(),
});

/* ---------- wall street ---------- */
export const Digest = z.object({
  /** ISO moment the digest was sent */
  at: z.string(),
  lede: z.string(),
  indices: z.array(z.tuple([z.string(), z.string(), z.number()])),
  themes: z.array(z.string()),
  movers: z.array(z.tuple([z.string(), z.number()])),
  earnings: z.array(z.tuple([z.string(), z.string(), z.string()])),
  /** [ISO moment, event] — shown in the user's time zone */
  macro: z.array(z.tuple([z.string(), z.string()])),
  sectors: z.array(z.tuple([z.string(), z.number()])),
  rates: z.array(z.tuple([z.string(), z.string(), z.string(), z.number()])),
  /** the digest's news with links (ORCA API) */
  news: z.array(z.object({ title: z.string(), url: z.string(), source: z.string().nullable() })).optional(),
  /** no stored digest for this edition yet */
  missing: z.boolean().optional(),
  /** the US trading date (YYYY-MM-DD) of this edition */
  tradingDate: z.string().optional(),
});
/** Fixed market schedule (New York wall time, US trading days). */
export const WallStreet = z.object({ pre: Digest, post: Digest, schedule: z.object({ pre: z.string(), post: z.string(), tz: z.string() }) });

/* ---------- social ---------- */
export const XPost = z.object({
  at: z.string(), who: z.string(), symbol: z.string(), text: z.string(), handle: z.string(),
  /** demo-only layers (the ORCA API stores the post, not Orca's notes about it) */
  role: z.string().optional(), why: z.string().optional(), relevance: z.number().optional(),
  /** coins the post is about; `mine` — one of the account's coins */
  coins: z.array(z.string()).optional(), mine: z.boolean().optional(),
  /** X verification: 'org' = gold (organisations), 'person' = blue. */
  verified: z.enum(['org', 'person']).optional(),
  /** Original text before Orca translated it. */
  original: z.string().optional(),
  stats: z.object({ replies: z.number(), reposts: z.number(), likes: z.number(), views: z.number() }).optional(),
  url: z.string(),
});
export const Video = z.object({
  image: z.string(), title: z.string(), at: z.string(), symbols: z.array(z.string()),
  /** demo-only (the ORCA API has the video link, title and coins) */
  channel: z.string().optional(), duration: z.string().optional(), theses: z.array(z.string()).optional(),
  url: z.string().optional(),
});
/** Per-coin X activity for today: important posts vs all mentions Orca saw. */
export const SocialCoin = z.object({
  symbol: z.string(), important: z.number(), mentions: z.number(), accounts: z.number(),
  spike: z.number(), // mentions vs the usual level, %
});
export const Social = z.object({ posts: z.array(XPost), videos: z.array(Video), coins: z.array(SocialCoin) });
/** One server page of X Radar (7 days): posts, the paging, the window's post counts per coin («*» = all). */
export const SocialXPage = z.object({
  available: z.boolean(), enabled: z.boolean(), disabledReason: z.string().optional(), posts: z.array(XPost), page: z.number(), pages: z.number(), total: z.number(), perPage: z.number(),
  counts: z.record(z.string(), z.number()),
  /** DEV mock only: per-coin X activity (the ORCA API does not collect it) */
  coins: z.array(SocialCoin),
});
/** One server page of YouTube Analytics (7 days). */
export const SocialYtPage = z.object({
  available: z.boolean(), enabled: z.boolean(), disabledReason: z.string().optional(), videos: z.array(Video), page: z.number(), pages: z.number(), total: z.number(), perPage: z.number(),
});

/* ---------- Orca AI ---------- */
export const Answer = z.object({
  /** stable id of a canned answer (language-independent) */
  id: z.string().optional(),
  lead: z.string().optional(),
  /** a follow-up the answer offers, e.g. «Указать позицию» */
  action: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('add-position'), symbol: z.string(), label: z.string() }),
    /** the question is about a feature the plan doesn't open: offer the plan, not the content */
    z.object({ kind: z.literal('upgrade'), feature: z.string(), label: z.string() }),
  ]).optional(),
  /** card titles instead of «Известно / Сценарий / Риски» (answers about Orca's plans) */
  labels: z.tuple([z.string(), z.string(), z.string()]).optional(),
  /** paid features the answer is built from (the server checks them against the plan) */
  requires: z.array(z.string()).optional(),
  known: z.string(),
  scenario: z.string(),
  risk: z.string(),
  sources: z.array(z.tuple([z.string(), z.string()])).optional(),
  /** a real Orca AI reply (ORCA API): Telegram-style HTML, sanitized by the server and again when shown */
  html: z.string().optional(),
});
export const Chat = z.object({
  id: z.string(), at: z.string(), title: z.string(), tag: z.string(), questions: z.array(z.string()),
});
export const OrbitNode = z.object({ icon: z.string(), name: z.string(), value: z.string(), question: z.string() });
export const OrcaAI = z.object({
  answers: z.record(Answer),
  fallback: Answer,
  chats: z.array(Chat),
  orbit: z.array(OrbitNode),
  placeholders: z.array(z.string()),
  askContexts: z.record(z.object({ name: z.string(), suggestions: z.array(z.string()) })),
  chatSuggestions: z.array(z.string()),
});

/* ---------- price history (date check for an approximate period) ---------- */
/** Mirrors the bot's one-shot Binance klines check: days in the period whose
 *  daily [low, high] (±3%) contains the price the user entered. */
/** NEW (web first): an estimated purchase price from market history —
 *  a day → that day's close; a month → the median daily close (+ the month's range). */
export const PriceEstimate = z.object({
  status: z.enum(['ok', 'unavailable', 'not_traded']),
  method: z.enum(['close', 'median_close']).optional(),
  price: z.number().optional(),
  low: z.number().optional(), high: z.number().optional(),
  /** the first traded day when the pair did not trade yet */
  listed: z.string().optional(),
});
export const HistoryCheck = z.object({
  status: z.enum(['single_match', 'multiple_matches', 'no_match', 'unavailable']),
  candidates: z.array(z.object({ day: z.string(), low: z.number(), high: z.number() })),
});

/* ---------- account (plan, limits, preferences) ---------- */
// Plan limits are NOT business rules in the frontend: they arrive with the
// account (later from the ORCA API / bot logic) and the UI only displays them.
export const Delivery = z.object({
  key: z.string(),
  icon: z.string(),
  title: z.string(),
  description: z.string(),
  enabled: z.boolean(),
  control: z.discriminatedUnion('type', [
    z.object({ type: z.literal('range'), min: z.number(), max: z.number(), value: z.number() }),
    z.object({ type: z.literal('choice'), options: z.array(z.string()), value: z.number() }),
    z.object({ type: z.literal('fixed'), label: z.string() }),
    /** a fixed list of hour options (exactly the bot's menu) */
    z.object({ type: z.literal('steps'), options: z.array(z.number()), value: z.number() }),
  ]),
  /** false: the user cannot switch it off (e.g. Plus «Идеи дня» in the bot) */
  toggle: z.boolean().optional(),
  /** shown from the account, changed in Telegram for now (not editable on the Web yet) */
  readOnly: z.boolean().optional(),
  /** next scheduled send (ISO), computed by the API from the interval; absent when off or event-driven */
  nextAt: z.string().optional(),
});
/*
 * Web ↔ Telegram sync rule (for the future backend).
 *
 * SHARED account state — one state for the bot and the site; a change in one
 * client shows in the other: coins («Мои монеты»), portfolio positions and their
 * purchases (if the backend stores lots), plan, the AI quota (Telegram + /ai +
 * the Orca bar spend one daily limit), delivery intervals, Price Alerts on/off,
 * Whale settings, X Radar on/off and mode, YouTube on/off, Wall Street on/off,
 * calendar importance threshold, language and any other product setting that
 * exists in both clients.
 *
 * WEB-ONLY — never sent to Telegram: theme, the site's display time zone, the
 * open panels / UI state, web layout prefs.
 * The display time zone only changes how the site shows times; it does NOT
 * change the bot's scheduler, a Telegram time zone or the bot's UTC logic. Making
 * the bot follow it would be a separate product decision.
 */
/**
 * What the plan opens, per feature (the bot's capabilities table): the UI
 * never decides it — it shows every feature and asks this before showing data
 * or allowing an action. `plan` = the lowest plan that opens the feature.
 */
export const Capability = z.object({ available: z.boolean(), plan: z.enum(['free', 'plus', 'premium']) });
const PlanLimit = z.object({ coins: z.number(), ai: z.number() });
export const Account = z.object({
  user: z.object({ name: z.string() }),
  /** activeUntil — ISO or null (Free); the AI quota resets at 00:00 UTC */
  plan: z.object({ id: z.string(), name: z.string(), coinLimit: z.number(), aiDailyLimit: z.number(), activeUntil: z.string().nullable() }),
  capabilities: z.record(Capability),
  /** the 5-day Plus trial — once per account, offered only on Free */
  trial: z.object({ available: z.boolean(), days: z.number() }),
  /** coin limits of the paid plans, for the «more coins» offer */
  coinLimits: z.object({ plus: z.number(), premium: z.number() }),
  /** coins and Orca AI questions a day of every plan (the API's plan_limits), for the plan cards */
  planLimits: z.object({ free: PlanLimit, plus: PlanLimit, premium: PlanLimit }),
  /** one AI quota for every question on the site; `resetAt` — next 00:00 UTC (ISO) */
  usage: z.object({ aiToday: z.number().nullable(), resetAt: z.string() }),
  coins: z.array(z.string()),
  availableCoins: z.array(z.string()),
  /** the account's language — SHARED with Telegram (web UI, bot messages, Orca AI answers); not a web-only pref */
  language: z.enum(['ru', 'en', 'de']),
  deliveries: z.array(Delivery),
  /** web-only preferences (never synced to Telegram) */
  web: z.object({
    /** IANA zone the site shows every time in — display only, not the bot's scheduler */
    displayTimezone: z.string(),
  }),
});

/* ---------- profile ---------- */
// Only what an ORCA account really has: its id, sign-in identities (linked or
// not — never provider ids), Web sessions, the subscription and payments.
/** a Web session: the server stores only when it started and when it ends (no device, IP or location) */
export const Session = z.object({ id: z.string(), createdAt: z.string().nullable(), expiresAt: z.string().nullable(), current: z.boolean() });
export const PaymentStatus = z.enum(['paid', 'pending', 'failed', 'canceled', 'expired', 'annulled', 'refunded', 'unknown']);
export const Payment = z.object({
  id: z.string(), at: z.string(), plan: z.string(), period: z.string(), amount: z.string(), method: z.string(), status: PaymentStatus,
});
const Linked = z.object({ linkedAt: z.string().nullable() }).nullable();
export const Profile = z.object({
  /** the internal ORCA account id (not a Telegram id) */
  accountId: z.number(),
  /** the site's display time zone (web-only, see the sync rule above) */
  timezone: z.string(),
  email: z.object({ address: z.string(), verified: z.boolean() }).nullable(),
  telegram: Linked,
  google: Linked,
  sessions: z.array(Session),
  billing: z.object({ payments: z.array(Payment) }),
  subscription: z.object({
    /** free = on the Free plan (also right after a paid plan or the Plus trial ended) */
    status: z.enum(['active', 'trial', 'expiring', 'free', 'paused', 'pending', 'failed']),
    plan: z.string(),
    period: z.string(),
    /** ISO, or null on Free */
    endsAt: z.string().nullable(),
    daysLeft: z.number(),
    /** total days of the current period — for the progress ring */
    days: z.number(),
    /** a payment created and not confirmed by the provider yet */
    pending: z.object({ id: z.string().optional(), amount: z.string(), method: z.string(), since: z.string() }).optional(),
    /** the 5-day Plus trial is one per account */
    trialUsed: z.boolean(),
    /** what just moved the account to Free: shown once as the bot's downgrade message */
    downgrade: z.object({ reason: z.enum(['trial', 'paid']), from: z.string() }).optional(),
  }),
  /** the partner program lives in a separate Telegram bot */
  referral: z.object({ bot: z.string() }),
  createdAt: z.string().nullable(),
});
/** Plan prices for the payment flow (from the product description). */
export const PlanOffer = z.object({
  id: z.string(), name: z.string(), note: z.string(),
  prices: z.array(z.object({ months: z.number(), price: z.number(), perMonth: z.number(), period: z.string().optional() })),
});
export const PlanOffers = z.array(PlanOffer);
/** Per locked feature: what it already found for the user — counts only, never the content. */
export const Teasers = z.record(z.string());
/** Full description of every plan + the comparison table (values: [Free, Plus, Premium]). */
export const PlanCatalog = z.object({
  plans: z.array(z.object({ id: z.enum(['free', 'plus', 'premium']), name: z.string(), price: z.string(), tagline: z.string(), features: z.array(z.string()) })),
  compare: z.array(z.object({ group: z.string(), label: z.string(), values: z.tuple([z.string(), z.string(), z.string()]) })),
  trial: z.string(),
  /** the plan cards: news interval, the few key points and what is not included (from the same catalog) */
  cards: z.object({
    audience: z.object({ free: z.string(), plus: z.string(), premium: z.string() }),
    news: z.object({ free: z.string(), plus: z.string(), premium: z.string() }),
    points: z.object({ free: z.array(z.string()), plus: z.array(z.string()), premium: z.array(z.string()) }),
    missing: z.object({ free: z.string(), plus: z.string(), premium: z.string() }),
  }),
});
/* ---------- alerts: blockchain watch (Whale Alerts) & Price Alerts ---------- */
// Same model as the bot: Whale Alerts watch up to `maxNetworks` networks and
// send transfers ≥ `threshold` USD; Price Alerts fire when a coin's 24 h
// change enters a new level. Rules, limits and flow classification are the
// API's — the UI shows what it gets and sends the user's choices back.
export const WalletEnd = z.object({ label: z.string(), type: z.enum(['exchange', 'wallet', 'unknown']) });
/** inflow = to a known exchange address, outflow = from one (bot's classification). */
export const FlowKind = z.enum(['inflow', 'outflow', 'exchange_to_exchange', 'wallet_transfer']);
export const WhaleTx = z.object({
  id: z.string(),
  /** ISO moment */
  at: z.string(),
  network: z.string(),
  token: z.string(),
  amount: z.number(),
  usd: z.number(),
  from: WalletEnd,
  to: WalletEnd,
  flow: FlowKind,
  hash: z.string(),
  url: z.string(),
});
export const ChainNetwork = z.object({ value: z.string(), label: z.string(), token: z.string() });
export const WhaleWatch = z.object({
  available: z.boolean(),
  enabled: z.boolean(),
  networks: z.array(z.string()),
  maxNetworks: z.number(),
  threshold: z.number(),
  options: z.object({ networks: z.array(ChainNetwork), thresholds: z.array(z.number()) }),
  /** transfers in all networks ≥ $1M for 7 days; the UI previews the user's filter on it */
  feed: z.array(WhaleTx),
});
export const PriceAlert = z.object({
  id: z.string(), at: z.string(), symbol: z.string(),
  /** 24 h change at the moment of the alert */
  change: z.number(),
  price: z.number(),
  /** signed level that was entered, e.g. −5 */
  level: z.number(),
  position: z.object({ qty: z.number(), entry: z.number() }).optional(),
});
export const PriceWatch = z.object({
  available: z.boolean(),
  enabled: z.boolean(),
  /** where the alerts go: the bot sends them to Telegram; a Web-only account sees them on the site only (web) */
  delivery: z.enum(['telegram', 'web', 'unavailable']).optional(),
  levels: z.array(z.number()),
  basis: z.string(),
  check: z.string(),
  /** current signed level per watched coin (0 = inside ±2 %) */
  coins: z.array(z.object({ symbol: z.string(), level: z.number() })),
  history: z.array(PriceAlert),
});
/** Where urgent alerts show up besides Telegram. */
export const NoticeChannels = z.object({ site: z.boolean() });
export const Alerts = z.object({
  whale: WhaleWatch, price: PriceWatch, channels: NoticeChannels,
});

/* ---------- notifications (bell) ---------- */
export const Notice = z.object({
  id: z.string(),
  kind: z.enum(['price', 'whale', 'digest', 'calendar', 'news', 'x', 'payment', 'forecast']),
  /** urgent = real-time alert: pops up on the site; the rest only goes to the bell */
  urgent: z.boolean(),
  title: z.string(),
  body: z.string(),
  at: z.string(),
  read: z.boolean(),
  to: z.string(),
  dir: z.enum(['up', 'down']).optional(),
  symbol: z.string().optional(),
});
export const Notices = z.array(Notice);
